import type { NavigationItem } from '@shared/gql/document-nodes'
import type { BreadCrumbItem } from 'components/Breadcrumb'
import type { FC } from 'react'
import type { NavigationItemTree } from 'types/gql/navigation-item-tree'
import type { NonNullableFields } from 'types/non-nullable-fields'

import { DeliveryMethod, FacetKey } from '@shared/gql/document-nodes'
import { Alert, AlertType, minWidthFromTheme } from '@sok/design-system'
import { Breadcrumb } from 'components/Breadcrumb'
import { GeneralError } from 'components/errors/ErrorPage'
import { CartSidebarLayout } from 'components/layout/CartSidebarLayout'
import { ProductList } from 'components/ProductList'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useProductSearch } from 'services/Search/hooks/use-product-search'
import { getFilterParams, SUPPORTED_FACETS } from 'services/Search/utils/filters'
import { SORT_OPTIONS_CATEGORY_LIST } from 'services/Search/utils/sort'
import styled, { css } from 'styled-components'
import { capitalize } from 'utils/common/capitalize'
import { searchNavigationItem } from 'utils/common/search-navigation-item'
import { useAlcoholAllowedForDeliveryArea } from 'utils/hooks/delivery/use-alcohol-allowed'
import { useDeliveryDate } from 'utils/hooks/delivery/use-delivery-date'
import { useStoreId } from 'utils/hooks/store/use-store-id'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { usePrevious } from 'utils/hooks/use-previous'
import { useURLSearchParams } from 'utils/hooks/use-url-search-params'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { pushData } from 'utils/tracking/custom-events/custom-events'
import { mapGa4Availabilities } from 'utils/tracking/event-product-mapper'
import { TrackingContext } from 'utils/tracking/interfaces/data-layer-events'
import { GA4_trackImpressions } from 'utils/tracking/track-product-impressions'
import { useGetInitialDeliverySelections } from 'views/delivery-modal/method-and-store/use-get-initial-delivery-selections'
import { Filters } from 'views/search-results/Filters'
import { SearchResultsTitle } from 'views/search-results/SearchResultsTitle'
import { Sort } from 'views/search-results/Sort'

import { CategoryLinks } from './CategoryLinks'
import { ClearQueryStringFilter } from './ClearQueryStringFilter'

/** Doesn't make sense to include the category filter on category pages */
export const FACETS_WITHOUT_CATEGORY = SUPPORTED_FACETS.filter(
  ({ key }) => key !== FacetKey.Category,
)

interface ProductResultsGridProps {
  'data-test-id': string
  categoryItems: BreadCrumbItem[]
  navigation?: NavigationItemTree
  page?: number
  slugs: string[]
}

export const ProductResultsGrid: FC<ProductResultsGridProps> = ({
  'data-test-id': dataTestId,
  categoryItems,
  navigation,
  page,
  slugs,
}) => {
  const { t } = useTranslation()
  const isClientSide = useIsClientSide()
  const { selectedStoreId } = useStoreId()
  const alcoholSellingAllowed = useAlcoholAllowedForDeliveryArea()
  const { initialDeliveryMethod } = useGetInitialDeliverySelections()
  const isHomeDelivery = initialDeliveryMethod === DeliveryMethod.HomeDelivery
  const [slug, parentSlug] = useMemo(
    () => [slugs.join('/') || '', slugs.slice(0, 2).join('/')],
    [slugs],
  )

  const {
    data: searchData,
    facets,
    loading: searchLoading,
    error,
    hasMore,
    loadingMore,
    loadMore,
    variables,
  } = useProductSearch({
    context: TrackingContext.PRODUCT_CATEGORY,
    facets: FACETS_WITHOUT_CATEGORY,
    includeAgeLimitedByAlcohol: true, // always show alcohol on category pages
    page,
    slug,
  })

  const searchResults = searchData?.store?.products?.items
  const searchTotal =
    searchData?.store?.products?.total !== undefined
      ? Number(searchData?.store?.products?.total)
      : undefined
  const previousTotal = usePrevious(searchTotal)

  useEffect((): void => {
    // Report category view only once
    if (navigation && !searchLoading && searchTotal && searchTotal !== previousTotal) {
      const trackingCategory = searchNavigationItem(navigation, slug, 'slug')?.slug || 'tuotteet'
      const trackingCategoryId = searchNavigationItem(navigation, slug, 'slug')?.id || ''

      pushData({
        viewedCategory: {
          slug: trackingCategory,
          id: trackingCategoryId,
        },
      })
    }
  }, [navigation, previousTotal, searchLoading, searchTotal, slug])

  // Index to track which products should be reported as impressions,
  // so that lazy-loaded results are not reported twice.
  const impressionSlice = useRef(0)

  // Reset index when search is updated and not only loading more results
  useEffect(() => {
    if (searchLoading && !loadingMore) {
      impressionSlice.current = 0
    }
  }, [loadingMore, searchLoading, variables?.from])

  const availabilityDate = useDeliveryDate()
  const listName = useMemo(() => {
    return searchNavigationItem(navigation || [], slug, 'slug')?.slug || 'tuotteet'
  }, [navigation, slug])
  // Report a single slice of Product Impressions to Analytics,
  // keeping track of lazy-loaded results.
  useEffect(() => {
    if (searchLoading || loadingMore) return

    const productArray = searchResults || []
    const start = variables?.from ?? impressionSlice.current
    const slicedDifference = productArray.slice(start)

    if (slicedDifference.length) {
      GA4_trackImpressions({
        listName,
        products: slicedDifference.map(mapGa4Availabilities(availabilityDate)),
        trackingContext: TrackingContext.PRODUCT_CATEGORY,
        listPosition: start,
      })
    }

    // Increment slice
    impressionSlice.current += slicedDifference.length
  }, [
    availabilityDate,
    listName,
    loadingMore,
    navigation,
    searchLoading,
    searchResults,
    slug,
    variables?.from,
  ])

  const { params } = useURLSearchParams()
  const searchParamString = useMemo(() => {
    const search = getFilterParams(params).toString()
    return search ? `?${search}` : ''
  }, [params])

  const [title, navLinks] = useMemo(() => {
    const navigationTitle =
      searchNavigationItem(navigation || [], slug, 'slug')?.name || t('All products')
    const navigationLinks = (
      searchNavigationItem(navigation || [], parentSlug, 'slug')?.children || navigation
    )?.filter(
      (link): link is NonNullableFields<NavigationItem, 'name' | 'slug'> =>
        !!link && !!link.name && !!link.slug,
    )
    return [navigationTitle, navigationLinks]
  }, [navigation, parentSlug, slug, t])

  const showLoadingIndicators = (searchLoading || searchTotal === undefined) && !error

  const resultsIncludeAlcohol = useMemo(
    /** do not display warning during SSR to prevent unnecessary flashing of content */
    () => (isClientSide && searchResults?.some((p) => p?.isAgeLimitedByAlcohol)) || false,
    [isClientSide, searchResults],
  )

  const handleInfiniteScroll = useCallback(async () => {
    if (hasMore) await loadMore()
  }, [hasMore, loadMore])

  return (
    <TrackingContextProvider
      searchProvider={variables?.searchProvider}
      listName={listName}
      trackingContext={TrackingContext.PRODUCT_CATEGORY}
    >
      <CartSidebarLayout data-test-id={dataTestId} showFooter={!hasMore}>
        <StyledBreadcrumb items={categoryItems} searchParamString={searchParamString} />

        {isHomeDelivery && resultsIncludeAlcohol && (
          <Alert
            message={t('Alcohol items only available for pickup')}
            type={AlertType.INFO}
            closable
          />
        )}

        {!!selectedStoreId &&
          alcoholSellingAllowed === false /** null while loading */ &&
          !isHomeDelivery &&
          resultsIncludeAlcohol && (
            <Alert
              message={t('Alcohol items not available in this store')}
              type={AlertType.INFO}
              closable
            />
          )}

        <SearchResultsTitle
          actions={
            <>
              <Filters loading={showLoadingIndicators} facets={facets} />
              <Sort sortOptions={SORT_OPTIONS_CATEGORY_LIST} />
            </>
          }
          titleText={title ? capitalize(title) : undefined}
          subtitle={
            !showLoadingIndicators && (
              <StyledItemCount data-test-id="product-result-total">
                {t('item', { count: searchTotal })}
              </StyledItemCount>
            )
          }
        />

        <ClearQueryStringFilter />

        <CategoryLinks
          items={navLinks}
          parentSlug={parentSlug}
          searchParamString={searchParamString}
          slug={slug}
        />

        {error ? (
          <StyledGeneralError />
        ) : (
          <ProductList
            hasMore={hasMore}
            loading={showLoadingIndicators}
            loadingMore={loadingMore}
            loadMore={handleInfiniteScroll}
            page={page}
            products={searchResults}
          />
        )}
      </CartSidebarLayout>
    </TrackingContextProvider>
  )
}

const StyledBreadcrumb = styled(Breadcrumb)(({ theme }) => ({
  padding: `0 ${theme.spacings.small}`,

  [minWidthFromTheme(theme).desktop]: {
    marginTop: 0,
    padding: 0,
  },
}))

const StyledGeneralError = styled(GeneralError)`
  height: auto;
  padding: ${({ theme }) => theme.spacings.xxxLarge} 0;
`

const StyledItemCount = styled.p`
  ${({ theme }) => css(theme.variants.body1.regular)};

  &::before {
    content: '(';
  }

  &::after {
    content: ')';
  }
`
